import { useValue } from '@aytm/app/insights/dashboard/components/hooks'
import { TagsEventType } from '@aytm/app/insights/dashboard/components/hooks/types'
import { useBlinkSubscription } from '@aytm/app/insights/dashboard/components/hooks/useBlink'
import { nullCellData } from '@aytm/app/insights/dashboard/helpers/values'
import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import { isBenchmark } from '@aytm/app/insights/dashboard/helpers/checks'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import classNames from 'classnames'
import React, { useMemo, useRef } from 'react'
import { CaptionType, EditorType, ItemType, Metrics } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import SurveyLink from '../SurveyLink'
import Tooltip from '../Tooltip'
import Ellipsis from './Ellipsis'
import Letter from './Letter'
import Tags from './Tags'
import mcss from './Value.module.scss'
import './Value.scss'

const CLASS = 'insights-portal-value'

const SURVEY_LINKS = ['surveyId', 'surveyTitle', 'surveys'] as const

type ValueInternalProps = {
  className?: string
  items?: Array<ItemType>
  index?: number
  editor?: EditorType
  caption?: CaptionType
  wait?: boolean
  showSigtest?: boolean
  sigtestCount?: number
  editorMode?: unknown
  subjectType?: string
  onItemPick?: (event: { uid: string; index: number }) => void
  onTagsUpdate?: (event: TagsEventType) => void
  onPopup?: () => void
}

const ValueDefaultProps = {
  sigtestCount: 5
}

const ValueInternal: React.FC<ValueInternalProps> = props => {
  const { sigtestCount: propsSigtestCount } = { ...ValueDefaultProps, ...props }
  const className = props.className || CLASS

  const { isSharedView } = staticStore

  const caption = useMemo(() => props.caption || ({} as CaptionType), [props.caption])

  const index = props.index ?? -1
  const attribute = caption.attribute || null

  const { getCellData } = useValue()
  const item = props.items?.[index] || null

  const metrics = caption.metrics || (null as Metrics)
  const defaultMetrics = caption.defaultMetrics || null
  const showSigtest = props.showSigtest ?? true

  const textRef = useRef<HTMLElement>(null)

  const targetRef = useBlinkSubscription(item?.uid)

  const cellData = useMemo(
    () => (getCellData ? getCellData(item, attribute, metrics, defaultMetrics) : nullCellData),
    [attribute, defaultMetrics, getCellData, item, metrics]
  )

  const {
    isDataPending,
    cellStyle,
    textStyle,
    significant,
    lowSample,
    sampleSize,
    responses,
    amount,
    attributeType,
    attributeTarget,
    value,
    text,
    textValue,
    selected,
    itemUid,
    letters
  } = cellData

  const effectiveLetters = useMemo(() => (showSigtest ? letters : []), [showSigtest, letters])

  const letterClick = useRef<ValueInternalProps['onItemPick']>(null)
  letterClick.current = event => {
    props.onItemPick?.(event)
    props.onPopup?.()
  }

  const updateTags: ValueInternalProps['onTagsUpdate'] = event => {
    props.onTagsUpdate?.(event)
  }

  const componentLetters = useMemo(() => {
    const letterClassName = classNames(`${className}-letters-item`, {
      [`${className}-letters-item-significant`]: significant
    })

    const result = effectiveLetters.map(v => (
      <Letter
        key={v.index}
        className={letterClassName}
        style={{ ...textStyle, minWidth: `${(v.letter?.length || 0) * 0.75}em` }}
        onClick={() => letterClick.current({ uid: v.uid, index: v.index })}
        letter={v.letter}
        tooltip={<div className={`${className}-letters-tooltip`}>{decode(v.text)}</div>}
      />
    ))
    const hiddenLetters = result.length > propsSigtestCount ? result.slice(propsSigtestCount) : []
    if (hiddenLetters.length > 0) {
      result.splice(propsSigtestCount)
      result.push(
        <Ellipsis
          key={'ellipsis'}
          className={letterClassName}
          style={textStyle}
          tooltip={<div className={`${className}-ellipsis-tooltip`}>{hiddenLetters}</div>}
        />
      )
    }
    return result
  }, [className, effectiveLetters, propsSigtestCount, significant, textStyle])

  const metaClassName =
    'meta' === attributeTarget
      ? classNames(`${className} ${className}-meta`, {
          [`${className}-meta-tags`]: 'tags' === attributeType
        })
      : className

  const content = useMemo(
    () =>
      (SURVEY_LINKS as unknown as string[]).includes(attributeType) ? (
        <SurveyLink value={value} disabled={isSharedView} showNames={'surveyTitle' === attributeType} />
      ) : (
        text
      ),
    [attributeType, value, isSharedView, text]
  )

  const textTooltip = useMemo(
    () =>
      textValue ? (
        <div className={`${className}-text-tooltip`}>
          {textValue}
          <br />
          {lowSample ? 'Low sample:' : 'Sample size:'}
          &nbsp;
          {sampleSize || 0}
          <br />
          {`Responses: ${Math.round(amount || 0)}`}
        </div>
      ) : null,
    [amount, className, lowSample, sampleSize, textValue]
  )

  const sampleSizeComponent = useMemo(
    () =>
      !value || isBenchmark({ uid: itemUid }) || 'meta' === attributeTarget || responses === sampleSize ? null : (
        <div className={`${className}-sample-size`}>{sampleSize}</div>
      ),
    [attributeTarget, className, itemUid, responses, sampleSize, value]
  )

  return (
    <div
      className={classNames(metaClassName, mcss['value'], {
        [`${className}-selected`]: selected,
        loading: isDataPending
      })}
      style={cellStyle}
      data-column-uid={itemUid}
      data-row-type={attributeTarget}
      data-row-id={attribute}
      data-metrics-id={metrics?.id}
      data-metrics-name={metrics?.name}
      ref={targetRef as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>['ref']}
    >
      {/* Index: {index} <br /> */}
      {'tags' === attributeType ? (
        <Tags
          className={`${className}-tags`}
          tags={Array.isArray(value) ? value : null}
          uid={itemUid}
          wait={props.wait || ['edit', 'change'].includes(props.editor?.type)}
          editorMode={props.editorMode && !isSharedView}
          onUpdate={updateTags}
        />
      ) : (
        <>
          <div
            ref={textRef as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>['ref']}
            className={`${className}-text-box`}
          >
            <div
              className={classNames(`${className}-text`, {
                [`${className}-text-significant`]: significant,
                [`${className}-text-low`]: lowSample && !significant
              })}
              style={textStyle}
            >
              {content}
            </div>
            {sampleSizeComponent}
          </div>
          <div className={`${className}-letters`}>{componentLetters}</div>
          {'meta' !== attributeTarget ? (
            <Tooltip
              position={'up-right'}
              onTarget={() => textRef.current}
              content={textTooltip}
              showOn={'click mouseenter'}
              hideOn={'click click:body wheel:body scroll:body mouseleave'}
            />
          ) : null}
        </>
      )}
    </div>
  )
}

const arePropsEqual = (pre: Partial<ValueInternalProps>, cur: Partial<ValueInternalProps>) =>
  (pre.wait === cur.wait && pre.wait) ||
  (pre.index === cur.index &&
    pre.items === cur.items &&
    pre.editor === cur.editor &&
    pre.caption?.metrics == cur.caption?.metrics &&
    pre.caption?.defaultMetrics == cur.caption?.defaultMetrics &&
    pre.showSigtest === cur.showSigtest)

const Value = React.memo(ValueInternal, arePropsEqual)

Value.displayName = 'Value'

export default Value
